import { makeid } from 'utils/helpers/makeid';
import useUser from 'utils/useUser';
import { isNotUndefined } from 'infrastructure/functions';
import { type ModuleType } from 'infrastructure/enums';
import { usePatientInfo } from 'infrastructure/hooks/patient/use-patient-info';
import { useMemo } from 'react';
import { useAccess } from 'infrastructure/hooks/access/useAccess';

import { getIndexByContext } from '../utils';
import GeneralCommentsControl from '../controls/general-comments';
import HoldingStatusControl from '../controls/holding-status';
import FollowUpAppointmentCompleteControl from '../controls/follow-up-appointment-complete';
import CallButtonsController from '../controls/call-buttons';
import SmsButtonsController from '../controls/sms-buttons';
import EmailSubscriptionControl from '../controls/email-subscription';
import EhrControl from '../controls/ehr';
import UnenrollPatientControl from '../controls/unenroll-patient';
import MonthlyInteractionControl from '../controls/monthly-interaction';
import ClinicalEscalationsControls from '../controls/clinical-escalations';
import RequestsControl from '../controls/requests';
import HospitalEscalationsControls from '../controls/hospital-escalations';
import ClinicalNotesControls from '../controls/clinical-notes-buttons';

interface Props {
  patientGuid: string;
  module: ModuleType;
}

export const useControlButtons = ({ patientGuid, module }: Props) => {
  const { isBiller, isPractice, isMobileDevice, isAdmin, isAgencyDoctor } =
    useUser();

  const { loading } = usePatientInfo({ patientGuid });

  const { isRpmModule, isCombinedModule, isCcmModule } = useAccess({
    patientGuid,
    module,
  });

  const getIndex = getIndexByContext({ module, isMobileDevice });

  const controlButtons = useMemo(() => {
    return [
      {
        index: getIndex({ defaultIndex: 0 }),
        key: makeid(5),
        component: GeneralCommentsControl,
        render: true,
      },
      {
        index: getIndex({ ccm: 1, rpm: 2, ccmRpm: 2, mob: 8 }),
        key: makeid(5),
        component: HoldingStatusControl,
        render: !isBiller,
      },
      {
        index: getIndex({ ccmRpm: 10, mob: 11, defaultIndex: 4 }),
        key: makeid(5),
        component: FollowUpAppointmentCompleteControl,
        render: isCcmModule || isCombinedModule,
      },
      {
        index: getIndex({ ccmRpm: 3, defaultIndex: 3 }),
        key: makeid(5),
        component: CallButtonsController,
        render: true,
      },
      {
        index: getIndex({
          ccm: 5,
          rpm: 6,
          ccmRpm: 6,
          mob: isCombinedModule ? 2 : 1,
        }),
        key: makeid(5),
        component: SmsButtonsController,
        render: true,
      },
      {
        index: getIndex({ ccm: 6, rpm: 8, ccmRpm: 8, mob: 10 }),
        key: makeid(5),
        component: EmailSubscriptionControl,
        render: true,
      },
      {
        index: getIndex({ ccm: 7, rpm: 1, ccmRpm: 1, mob: 5 }),
        key: makeid(5),
        component: EhrControl,
        render: isAdmin || isAgencyDoctor,
      },
      {
        index: getIndex({ ccmRpm: 11, defaultIndex: 8 }),
        key: makeid(5),
        component: UnenrollPatientControl,
        render: !isMobileDevice,
      },
      {
        index: getIndex({ ccmRpm: 5, mob: 9, defaultIndex: 5 }),
        key: makeid(5),
        component: MonthlyInteractionControl,
        render: isCombinedModule || isRpmModule,
      },
      {
        index: getIndex({ ccmRpm: 4, mob: 6, defaultIndex: 7 }),
        key: makeid(5),
        component: ClinicalEscalationsControls,
        render: (isRpmModule || isCombinedModule) && !isPractice,
      },
      {
        index: getIndex({
          ccmRpm: 9,
          mob: isCombinedModule ? 1 : 3,
          defaultIndex: 9,
        }),
        key: makeid(5),
        component: RequestsControl,
        render: isCombinedModule || isRpmModule,
      },
      {
        index: getIndex({ ccmRpm: 7, mob: 6, defaultIndex: 10 }),
        key: makeid(5),
        component: HospitalEscalationsControls,
        render: (isRpmModule || isCombinedModule) && !isPractice,
      },
      {
        index: getIndex({ ccmRpm: 12, mob: 8, defaultIndex: 9 }),
        key: makeid(5),
        component: ClinicalNotesControls,
        render: (isRpmModule || isCombinedModule) && !isPractice,
      },
    ]
      .filter(({ render, index }) => render && isNotUndefined(index))
      .sort((a, b) => a.index! - b.index!);
  }, [patientGuid]);

  return {
    loading,
    controlButtons,
  };
};
